import type { JobDetails } from '@seek/hirer-job-information';
import {
  isBefore,
  parseISO,
  sub,
  differenceInDays,
  format,
  addDays,
} from 'date-fns';
import { types, type Instance, getRoot, flow } from 'mobx-state-tree';

import { JobStatus } from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import type { PageConfig } from 'src/libs/PageConfig';

import { DEFAULT_ID } from '../../constants/job';
import expireJob from '../../graphql/hirer/expireJob';
import { getItem, setItem } from '../../utils/localStorage';
import type { IJobApplicationStore } from '../JobApplicationStore';
import type { IStore } from '../Store';
import type { IUserStore } from '../UserStore';

import JobInformation from './JobInformation';
import { getJobDetails } from './utils/getJobDetails';

const OLD_JOB_INTERCOM_MODAL_FOR_ADVERTISER =
  'cm-ui-completed-intercom-survey-23928508';

const Job = types
  .model('Job', {
    positionId: types.optional(types.number, DEFAULT_ID),
    jobInformation: types.optional(JobInformation, {}),
    isOldJobIntercomVisited: types.optional(
      types.boolean,
      Boolean(getItem(OLD_JOB_INTERCOM_MODAL_FOR_ADVERTISER)),
    ),
  })
  .views((self) => ({
    get showOldJobIntercom() {
      return (
        isBefore(
          parseISO(self.jobInformation.listingStartedDate),
          sub(Date.now(), { years: 2, months: 1 }),
        ) &&
        !self.isOldJobIntercomVisited &&
        false // soft disable showOldjobIntercom, we will review again in CMGT-2799
      );
    },

    get jobDetails(): JobDetails | undefined {
      return getJobDetails(self.jobInformation);
    },

    get jobCountry(): string {
      return self.jobInformation.jobCountry || '';
    },

    get expiryDate() {
      try {
        const { numberOfDaysRemaining } = self.jobInformation;
        if (numberOfDaysRemaining) {
          return format(
            addDays(new Date(), numberOfDaysRemaining),
            'dd/MM/yyyy',
          );
        }
      } catch (_e) {}

      return '';
    },

    get isActive() {
      return self.jobInformation.status === JobStatus.ACTIVE;
    },

    get isExpired() {
      return self.jobInformation.status === JobStatus.EXPIRED;
    },

    get isPending() {
      return [
        JobStatus.PENDING,
        JobStatus.PENDING_ACCOUNT_APPROVAL,
        JobStatus.SUSPENDED,
      ].includes(self.jobInformation.status);
    },

    get userStore(): IUserStore {
      return (getRoot(self) as IStore).userStore;
    },

    get jobApplicationStore(): IJobApplicationStore {
      return (getRoot(self) as IStore).jobApplicationStore;
    },

    get advertiserId(): string {
      return (getRoot(self) as IStore).advertiserId;
    },

    get seekAdvertiserId(): string {
      return (getRoot(self) as IStore).seekAdvertiserId;
    },
  }))
  .actions((self) => {
    const expire = flow(function* (
      addCustomError: PageConfig['dataDogRum']['addCustomError'],
    ) {
      const rootStore = getRoot(self) as IStore;
      try {
        yield expireJob(
          `${self.jobInformation.jobId}`,
          rootStore.seekAdvertiserId,
        );
        self.jobInformation.status = JobStatus.EXPIRED;
        return true;
      } catch (error: any) {
        addCustomError({
          error,
          context: {
            activity: 'EXPIRE_JOB_AD',
            description: 'Error attempting: expireJob',
            jobId: self.jobInformation.jobId,
            seekAdvertiserId: rootStore.seekAdvertiserId,
          },
        });
        return false;
      }
    });

    const updateExpiryDate = flow(function* (
      expiryDate: Date,
      addCustomError: PageConfig['dataDogRum']['addCustomError'],
    ) {
      const rootStore = getRoot(self) as IStore;
      const expiryDateISOString = expiryDate.toISOString();
      try {
        yield expireJob(
          `${self.jobInformation.jobId}`,
          rootStore.seekAdvertiserId,
          expiryDateISOString,
        );
        self.jobInformation.expiredDate = expiryDateISOString;
        self.jobInformation.numberOfDaysRemaining = differenceInDays(
          expiryDate,
          new Date(),
        );
        self.jobInformation.status = JobStatus.ACTIVE;
        return true;
      } catch (error: any) {
        addCustomError({
          error,
          context: {
            activity: 'UPDATE_EXPIRY_DATE',
            description: 'Error attempting: expireJob',
            jobId: self.jobInformation.jobId,
            seekAdvertiserId: rootStore.seekAdvertiserId,
            expiryDateISOString,
          },
        });
        return false;
      }
    });

    const markIntercomForOldJobAsVisited = () => {
      setItem(OLD_JOB_INTERCOM_MODAL_FOR_ADVERTISER, 'true');
      self.isOldJobIntercomVisited = true;
    };

    return {
      expire,
      updateExpiryDate,
      markIntercomForOldJobAsVisited,
    };
  });

export interface IJob extends Instance<typeof Job> {}

export default Job;
